import {Trans, useTranslation} from "react-i18next";
import {useRouteMatch} from "react-router-dom";

import {IMonthCityStats} from "../../../../../api/src/db_queries/month_city_stats_query";
import {IMonthCityTypeStats} from "../../../../../api/src/db_queries/month_city_type_stats_query";
import {mapCitySlugToRegionType} from "../../../../../config/regions";
import {originDate} from "../../../../../utils/dates";
import {useAppSelector} from "../../../../../utils/hooks/store_hooks";
import {FilterOfferType} from "../../../common/app/constants/enums";
import {mapPathnameToOfferType} from "../../../common/app/routing/offer_type";
import {LineColumnChartCard} from "../../../components/charts/chart_cards/LineColumnChartCard";
import {Grid} from "../../../components/Grid";
import {withDateGuard} from "../../../components/hoc/withDateGuard";
import {withGTM} from "../../../components/hoc/withGTM";
import {ChartsLayout as Layout} from "../../../components/layout/charts_layout/ChartsLayout";
import {selectAuthStatus} from "../../auth/auth_slice";
import {selectLatestEntry} from "../../ssr/redux/latest_entry_slice";
import {pageviewAlgolyticsHit} from "../../tracking/algolytics/pageview_hits";
import {usePageview} from "../../tracking/use_pageview";
import {ViewType} from "../../tracking/view_type";
import {useGetChartsMonthCityStatsQuery, useGetChartsMonthCityTypeStatsQuery} from "../api/charts_platform_api";
import {useChartsRegionId} from "../hooks/use_charts_region_id";
import {useSelectedRegionDisplay} from "../hooks/use_selected_region_display";
import {getCityViewConfig} from "../utils/city_view_config";
import {mapRtkQueryStatusToRequestStatus} from "../utils/rtq_status_to_request_status";

export const LocationSummary = withDateGuard(
    withGTM(() => {
        const {path} = useRouteMatch();
        const {t} = useTranslation();

        const {citySlug} = useSelectedRegionDisplay();
        const regionType = mapCitySlugToRegionType(citySlug);
        const routeOfferType = mapPathnameToOfferType(path);
        const {filterOfferType, propertyType, useTypeStats} = getCityViewConfig(routeOfferType);

        usePageview(() => {
            pageviewAlgolyticsHit({
                citySlug,
                offerType: routeOfferType,
                regionType,
                viewType: ViewType.LOCATION_SUMMARY
            });
        });

        const {isAuthorized} = useAppSelector(selectAuthStatus);
        const {currentDate} = useAppSelector(selectLatestEntry);
        const {regionId, hasChartsAccess, isRegionIdReady} = useChartsRegionId();
        const isPermitted = isAuthorized && hasChartsAccess;
        const skipChartsFetch = !isRegionIdReady || regionId == null;

        const chartsQueryParams = {
            region_id: regionId!,
            date_start: originDate,
            date_end: currentDate ?? "",
            scenario: "dashboard"
        };

        const {
            data: cityStatsData = [],
            isError: isCityStatsError,
            isSuccess: isCityStatsSuccess
        } = useGetChartsMonthCityStatsQuery(chartsQueryParams, {
            skip: skipChartsFetch || useTypeStats
        });

        const {
            data: typeStatsData = [],
            isError: isTypeStatsError,
            isSuccess: isTypeStatsSuccess
        } = useGetChartsMonthCityTypeStatsQuery(
            {
                ...chartsQueryParams,
                offer_type: filterOfferType ?? FilterOfferType.FLATS
            },
            {skip: skipChartsFetch || !useTypeStats}
        );

        const chartData = useTypeStats ? typeStatsData : cityStatsData;
        const dataStatus = mapRtkQueryStatusToRequestStatus(
            useTypeStats ? isTypeStatsError : isCityStatsError,
            useTypeStats ? isTypeStatsSuccess : isCityStatsSuccess
        );
        const chartIdPrefix = `location-summary-${propertyType}`;

        return (
            <Layout>
                <Grid>
                    <LineColumnChartCard<IMonthCityStats | IMonthCityTypeStats>
                        chartId={`${chartIdPrefix}-chart1`}
                        title={<Trans>{t("location_summary.demand_supply_balance.title")}</Trans>}
                        helper={t("location_summary.demand_supply_balance.helper")}
                        data={chartData}
                        dataStatus={dataStatus}
                        columnFields={["available_offers"]}
                        lineFields={["sold", "added"]}
                        showLineMarkers
                        customFieldColors={{
                            added: "red",
                            sold: "blue"
                        }}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />

                    <LineColumnChartCard<IMonthCityStats | IMonthCityTypeStats>
                        chartId={`${chartIdPrefix}-chart2`}
                        title={<Trans>{t("location_summary.pricing_competitive_situation.title")}</Trans>}
                        helper={t("location_summary.pricing_competitive_situation.helper")}
                        data={chartData}
                        dataStatus={dataStatus}
                        columnFields={["demand"]}
                        lineField="avg_price_m2"
                        customFieldColors={{
                            avg_price_m2: "blue"
                        }}
                        tooltipUnits={{
                            line: "zł",
                            column: "%"
                        }}
                        columnValueMultiplier={100}
                        showLineMarkers
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />
                </Grid>
            </Layout>
        );
    })
);
